// ParentChildren.js
import React, { useEffect, useState } from 'react';
import API from './api';
import {
  FaChild,
  FaUserMd,
  FaMapMarkerAlt,
  FaHeartbeat,
  FaVideo,
} from 'react-icons/fa';

/** -------- Helpers -------- */

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
}

function calculateAge(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let years = now.getFullYear() - dob.getFullYear();
  let months = now.getMonth() - dob.getMonth();

  if (now.getDate() < dob.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  if (years <= 0 && months <= 0) return 'Newborn';
  if (years <= 0) return `${months} month${months === 1 ? '' : 's'}`;
  if (years < 3) {
    const totalMonths = years * 12 + months;
    return `${years}y (${totalMonths} mo)`;
  }
  return `${years} years`;
}

function genderLabel(gender) {
  if (!gender) return 'Not specified';
  if (gender === 'male') return 'Boy';
  if (gender === 'female') return 'Girl';
  return 'Other / prefer not to say';
}

function buildAddress(child) {
  const parts = [
    child.address_street,
    [child.address_city, child.address_state].filter(Boolean).join(', '),
    [child.address_zip, child.address_country].filter(Boolean).join(' '),
  ].filter((p) => p && p.trim() !== '');
  return parts.length ? parts.join(' • ') : 'Not provided';
}

function ServiceTypeBadges({ service_types }) {
  if (!service_types || service_types.length === 0) {
    return (
      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-500 border border-dashed border-gray-300">
        No discipline assigned yet
      </span>
    );
  }

  const items = Array.from(new Set(service_types)); // unique

  return (
    <div className="flex flex-wrap gap-1">
      {items.map((code) => {
        let cls =
          'bg-gray-100 text-gray-700 border border-gray-300 text-[10px]';
        let label = code;

        if (code === 'EI') {
          cls =
            'bg-blue-50 text-blue-800 border border-blue-200 text-[10px]';
          label = 'EI';
        } else if (code === 'PI') {
          cls =
            'bg-orange-50 text-orange-800 border border-orange-200 text-[10px]';
          label = 'PI';
        } else if (code === 'ABA') {
          cls =
            'bg-purple-50 text-purple-800 border border-purple-200 text-[10px]';
          label = 'ABA';
        } else if (code === 'OT') {
          cls =
            'bg-teal-50 text-teal-800 border border-teal-200 text-[10px]';
          label = 'OT';
        } else if (code === 'PT') {
          cls =
            'bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px]';
          label = 'PT';
        }

        return (
          <span
            key={code}
            className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold ${cls}`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

function PreferenceBadges({ telehealth_ok, provider_gender_preference }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {/* Gender preference */}
      {provider_gender_preference && provider_gender_preference !== 'any' && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-pink-50 text-pink-700 border border-pink-200">
          <FaUserMd className="mr-1" size={10} />
          Prefers {provider_gender_preference === 'male' ? 'male' : 'female'} provider
        </span>
      )}

      {/* Telehealth – prikazujemo samo pozitivan info, nema više "not allowed" */}
      {telehealth_ok && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border bg-green-50 text-green-700 border-green-200">
          <FaVideo className="mr-1" size={10} />
          Telehealth OK
        </span>
      )}
    </div>
  );
}

/** -------- Main component -------- */

function ParentChildren() {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadChildren() {
      setLoading(true);
      setError('');
      try {
        const res = await API.get('/parent/children');
        if (!isMounted) return;
        setChildren(res.data || []);
      } catch (err) {
        console.error('Failed to load parent children:', err);
        if (!isMounted) return;
        setError(
          'Unable to load child information right now. Please try again later or contact your provider.'
        );
        setChildren([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadChildren();
    return () => {
      isMounted = false;
    };
  }, []);

  const hasChildren = children && children.length > 0;

  return (
    <div className="w-full px-2 sm:px-4">
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-900">My children</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            View child profiles connected to this email, including their
            disciplines, default providers, service period and preferences.
          </p>
        </div>
        {hasChildren && (
          <div className="flex items-center gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-full">
            <FaChild className="text-sky-500" size={12} />
            <span>
              {children.length} child
              {children.length > 1 ? 'ren' : ''} linked to this parent account
            </span>
          </div>
        )}
      </div>

      {/* Loading / error / empty states */}
      {loading && (
        <div className="w-full py-8 flex justify-center">
          <div className="text-sm text-slate-500 animate-pulse">
            Loading child profiles…
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="w-full py-4">
          <div className="border border-red-100 bg-red-50 text-red-700 text-xs rounded-xl px-3 py-2">
            {error}
          </div>
        </div>
      )}

      {!loading && !error && !hasChildren && (
        <div className="w-full py-6">
          <div className="border border-slate-100 bg-slate-50 rounded-2xl px-4 py-3 text-xs text-slate-600">
            <p className="font-semibold text-slate-800 mb-1">
              No children are currently linked to this email.
            </p>
            <p>
              If you believe this is a mistake, please contact your early
              intervention provider so they can update your records.
            </p>
          </div>
        </div>
      )}

      {/* Cards grid */}
      {!loading && !error && hasChildren && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {children.map((child) => {
            const fullName = `${(child.last_name || '').trim()}, ${(child.first_name || '').trim()}`.replace(
              /^,\s*/,
              ''
            );
            const ageLabel = calculateAge(child.dob);
            const address = buildAddress(child);
            const serviceRange =
              child.service_start_date || child.service_end_date
                ? `${formatDate(child.service_start_date)} — ${
                    child.service_end_date
                      ? formatDate(child.service_end_date)
                      : 'ongoing'
                  }`
                : 'Not specified';

            const isActive = child.is_active;
            const initials = (
              (child.first_name || '').charAt(0) +
              (child.last_name || '').charAt(0)
            )
              .toUpperCase()
              .trim();

            const currentTypes = child.current_service_types || [];
            const pastTypes = child.past_service_types || [];

            const hasAnyDisciplines =
              (currentTypes && currentTypes.length > 0) ||
              (pastTypes && pastTypes.length > 0);

            return (
              <div
                key={child.id}
                className="bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-150 flex flex-col p-4"
              >
                {/* Top row: avatar + name + status */}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-sm font-bold">
                    {initials || <FaChild />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-sm text-slate-900 truncate">
                        {fullName || 'Unnamed child'}
                      </span>
                      {child.uci && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-50 text-[10px] text-slate-700 border border-slate-200">
                          UCI: {child.uci}
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          isActive
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-slate-50 text-slate-400 border border-slate-200'
                        }`}
                      >
                        <FaHeartbeat className="mr-1" size={10} />
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      {ageLabel && (
                        <span>
                          Age: <span className="font-medium">{ageLabel}</span>
                        </span>
                      )}
                      {child.dob && (
                        <span>Dob: {formatDate(child.dob)}</span>
                      )}
                      {child.gender && (
                        <span>{genderLabel(child.gender)}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="my-3 border-t border-dashed border-slate-100" />

                {/* Disciplines: current + past */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                      Disciplines
                    </span>
                  </div>

                  {!hasAnyDisciplines ? (
                    <ServiceTypeBadges service_types={[]} />
                  ) : (
                    <div className="space-y-1">
                      {currentTypes.length > 0 && (
                        <div className="flex items-start gap-1 text-[11px]">
                          <span className="text-slate-500 mt-0.5 min-w-[52px]">
                            Current:
                          </span>
                          <ServiceTypeBadges service_types={currentTypes} />
                        </div>
                      )}
                      {pastTypes.length > 0 && (
                        <div className="flex items-start gap-1 text-[11px]">
                          <span className="text-slate-400 mt-0.5 min-w-[52px]">
                            Past:
                          </span>
                          <ServiceTypeBadges service_types={pastTypes} />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Provider + preferences */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                      Default provider
                    </span>
                  </div>
                  <div className="text-xs text-slate-700 flex items-center gap-1">
                    <FaUserMd className="text-sky-600" size={12} />
                    {child.default_teacher_name ? (
                      <span>{child.default_teacher_name}</span>
                    ) : (
                      <span className="text-slate-400 italic">
                        Not assigned yet
                      </span>
                    )}
                  </div>
                  <PreferenceBadges
                    telehealth_ok={child.telehealth_ok}
                    provider_gender_preference={
                      child.provider_gender_preference
                    }
                  />
                </div>

                {/* Service period */}
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                      Service period
                    </span>
                  </div>
                  <div className="text-xs text-slate-700">
                    {serviceRange}
                  </div>
                </div>

                {/* Address */}
                <div className="mb-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                      Home address
                    </span>
                  </div>
                  <div className="text-xs text-slate-700 flex items-start gap-1">
                    <FaMapMarkerAlt className="mt-0.5 text-rose-500" size={11} />
                    <span>{address}</span>
                  </div>
                </div>

                {/* Footer hint */}
                <div className="mt-3 pt-2 border-t border-dashed border-slate-100 text-[10px] text-slate-400">
                  To view detailed visit history and notes, open the
                  <span className="font-semibold text-slate-600">
                    {' '}
                    Visits Calendar
                  </span>{' '}
                  tab in the menu.
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ParentChildren;



